import { useState } from 'preact/hooks'
import { InitData } from '../../core'
import { t } from '../../core/i18n'
import { ScheduleWidget } from './ScheduleWidget'
import { WeeklyScheduleWidget } from '../weekly-schedule/WeeklyScheduleWidget'

interface Props {
  initData: InitData
  holdTypeSlugs?: string[]
  templateId?: string | null
  weeks?: number | null
  /** Heading above the widget. Widgets carry no title of their own by default. */
  title?: string | null
  /** Render the Classes/Calendar switch. Off = the plain list, as before. */
  viewToggle?: boolean
  /** Which view the switch starts on. */
  defaultView?: 'list' | 'calendar'
  /** guests-only="true": gæste-fokuseret booking (stepperen vises åben). */
  guestsOnly?: boolean
}

/**
 * SchedulePanel — the studio site's Classes/Calendar switch, for embedded pages.
 *
 * The native booking page lets a visitor flip between a class list and the week
 * calendar. Embedded sites previously had to pick one of the two widgets and
 * live with it, which is why an embedded schedule never looked like the real
 * site. This wraps the two existing widgets and swaps between them; each keeps
 * its own data fetching, so nothing about either view changes.
 *
 * Without `viewToggle` it renders exactly what `<holdstyring-schedule>` always
 * rendered, so existing embeds are untouched.
 */
export function SchedulePanel({
  initData,
  holdTypeSlugs = [],
  templateId = null,
  weeks = null,
  title = null,
  viewToggle = false,
  defaultView = 'list',
  guestsOnly = false,
}: Props) {
  const s = t(initData.locale)
  const [view, setView] = useState<'list' | 'calendar'>(defaultView)

  // Legacy shortcut: a bare `<holdstyring-schedule>` renders exactly the list it
  // always did. ⛔ `defaultView` must be part of the condition — without it,
  // `default-view="calendar"` on its own was swallowed silently and the visitor
  // got the flat list with no hint that the attribute did nothing
  // (ship finding, 28-09-2026). `defaultView` defaults to 'list', so every
  // existing embed still takes this path.
  if (!viewToggle && !title && defaultView === 'list') {
    return (
      <ScheduleWidget
        initData={initData}
        holdTypeSlugs={holdTypeSlugs}
        templateId={templateId}
        weeks={weeks}
        guestsOnly={guestsOnly}
      />
    )
  }

  return (
    <div>
      <style>{styles}</style>
      {(title || viewToggle) && (
        <div class="hsw-panel-header">
          {title && <h2 class="hsw-panel-title">{title}</h2>}
          {viewToggle && (
            <div class="hsw-view-toggle" role="group">
              <button
                type="button"
                class={`hsw-view-btn${view === 'list' ? ' hsw-view-active' : ''}`}
                aria-pressed={view === 'list'}
                onClick={() => setView('list')}
              >
                {s.viewList}
              </button>
              <button
                type="button"
                class={`hsw-view-btn${view === 'calendar' ? ' hsw-view-active' : ''}`}
                aria-pressed={view === 'calendar'}
                onClick={() => setView('calendar')}
              >
                {s.viewCalendar}
              </button>
            </div>
          )}
        </div>
      )}

      {view === 'calendar' ? (
        // The calendar view has no template filter of its own; hold-type slugs
        // are the filter both views share. layout="grid" renders the studio's
        // 7-column weekday ugekalender rather than the day-grouped list.
        <WeeklyScheduleWidget
          initData={initData}
          holdTypeSlugs={holdTypeSlugs}
          weekNav={true}
          layout="grid"
          guestsOnly={guestsOnly}
        />
      ) : (
        <ScheduleWidget
          initData={initData}
          holdTypeSlugs={holdTypeSlugs}
          templateId={templateId}
          weeks={weeks}
        />
      )}
    </div>
  )
}

const styles = `
  .hsw-panel-header {
    max-width: 1100px;
    margin: 0 auto 1.5rem auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .hsw-panel-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }
  .hsw-view-toggle {
    display: inline-flex;
    margin-left: auto;
    border: 1px solid currentColor;
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
  }
  .hsw-view-btn {
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    background: transparent;
    border: 0;
    padding: 0.5rem 1.1rem;
    line-height: 1;
    opacity: 0.7;
    color: inherit;
  }
  .hsw-view-btn:hover {
    opacity: 1;
  }
  .hsw-view-btn.hsw-view-active {
    opacity: 1;
    background: var(--yoga-primary, currentColor);
    color: #fff;
  }
`
